import { ApiError } from "@/shared/domain/api-error";
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  setTokens,
  type TokenPair,
} from "@/shared/infrastructure/auth/token-storage";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export interface HttpRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the Bearer token and the 401 refresh-retry cycle (login, onboarding, refresh itself). */
  skipAuth?: boolean;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * O access token dura só 15min (JWT_EXPIRES_IN do backend) — a rotação é feita sob
 * demanda no retry de um 401, não por um timer, pra não manter sessão viva sem uso real.
 * `refreshInFlight` deduplica chamadas concorrentes (várias queries batendo 401 ao mesmo tempo).
 */
async function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) return false;

      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) {
          clearAuthStorage();
          return false;
        }
        const tokens: TokenPair = await response.json();
        setTokens(tokens);
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function parseErrorBody(response: Response): Promise<{ message: string; details?: unknown }> {
  const text = await response.text();
  try {
    const data = JSON.parse(text);
    return { message: data.message ?? data.error ?? "Erro na requisição à API", details: data };
  } catch {
    return { message: text || "Erro na requisição à API" };
  }
}

async function performFetch(path: string, options: HttpRequestOptions): Promise<Response> {
  const { body, skipAuth, headers, ...rest } = options;
  const isFormData = body instanceof FormData;
  const token = skipAuth ? null : getAccessToken();

  const finalHeaders: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  const cleanPath = path.startsWith("/api/") ? path.slice(4) : path;
  const normalizedPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${normalizedPath}`;

  return fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: isFormData ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export async function httpClient<T = unknown>(path: string, options: HttpRequestOptions = {}): Promise<T> {
  let response = await performFetch(path, options);

  if (response.status === 401 && !options.skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await performFetch(path, options);
    }
  }

  if (!response.ok) {
    const { message, details } = await parseErrorBody(response);
    throw new ApiError(message, response.status, details);
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export async function httpClientBlob(path: string, options: HttpRequestOptions = {}): Promise<Blob> {
  let response = await performFetch(path, options);

  if (response.status === 401 && !options.skipAuth) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await performFetch(path, options);
    }
  }

  if (!response.ok) {
    const { message, details } = await parseErrorBody(response);
    throw new ApiError(message, response.status, details);
  }

  return response.blob();
}
